'use client';

import { TabGroup, TabList } from '@headlessui/react';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { isInfoHiddenForCinema } from '@/components/navigation/Navigation';
import NavigationDropdownMobile, { NavigationDropdownItem } from '@/components/navigation/NavigationDropdownMobile';
import NavigationItemMobile from '@/components/navigation/NavigationItemMobile';
import ShopLinksDropdownMobile from '@/components/navigation/ShopLinksDropdownMobile';

import { getShopLinks, resolveShopLinkHref } from '@cineamo/api/models/website-configuration/shop-link-helper';
import { WebsiteNavigation } from '@cineamo/api/models/website-configuration/WebsiteNavigaton.enum';

import { isNavigationVisible } from '@/helpers/navigation-helper';
import { planMobileNavigation } from '@cineamo/ui/src/helpers/mobile-navigation-helper';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';

import { IconBar24 } from '@cineamo/ui/src/icons/ic-bar';
import { IconBarSolid24 } from '@cineamo/ui/src/icons/ic-barSolid';
import { IconDeals24 } from '@cineamo/ui/src/icons/ic-deals';
import { IconDealsSolid24 } from '@cineamo/ui/src/icons/ic-dealsSolid';
import { IconHome24 } from '@cineamo/ui/src/icons/ic-home';
import { IconHomeSolid24 } from '@cineamo/ui/src/icons/ic-homeSolid';
import { IconInfo24 } from '@cineamo/ui/src/icons/ic-info';
import { IconInfoSolid24 } from '@cineamo/ui/src/icons/ic-infoSolid';
import { IconKebapMenu24 } from '@cineamo/ui/src/icons/ic-kebapMenu';
import { IconMegaphone24 } from '@cineamo/ui/src/icons/ic-megaphone';
import { IconMegaphoneSolid24 } from '@cineamo/ui/src/icons/ic-megaphoneSolid';
import { IconPopcornBag24 } from '@cineamo/ui/src/icons/ic-popcornBag';
import { IconProfile24 } from '@cineamo/ui/src/icons/ic-profile';
import { IconProgram24 } from '@cineamo/ui/src/icons/ic-program';
import { IconProgramSolid24 } from '@cineamo/ui/src/icons/ic-programSolid';
import { IconStar24 } from '@cineamo/ui/src/icons/ic-star';
import { IconStarSolid24 } from '@cineamo/ui/src/icons/ic-starSolid';
import { IconVoucher24 } from '@cineamo/ui/src/icons/ic-voucher';

import { usePathname } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type NavigationMobileProps = {
    hasShowingTags: boolean;
    hasAnnouncements: boolean;
};

function NavigationMobile(props: NavigationMobileProps): React.JSX.Element {
    const { hasShowingTags, hasAnnouncements } = props;
    const domainConfiguration = useDomainConfiguration();

    const t = useTranslations();

    // const { hideNavigationMobile } = useWebappStore();
    const isMobile = useIsMobile();

    const pathname = usePathname();

    // Resolve the portal target (#mobile-nav, rendered in BaseLayout) reactively instead of
    // once during render. The embedded pages live in a separate root layout (their own <html>),
    // so navigating back re-creates the #mobile-nav node — a one-time lookup at render time can
    // miss it and leave the bottom navigation unmounted. Re-resolve on every navigation and on
    // bfcache restores (browser back/forward, which don't trigger a React re-render).
    const [mobileMenuContainer, setMobileMenuContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        const resolveContainer = () => setMobileMenuContainer(document.getElementById('mobile-nav'));
        resolveContainer();
        window.addEventListener('pageshow', resolveContainer);
        return () => window.removeEventListener('pageshow', resolveContainer);
    }, [pathname]);

    // Hide component on movie detail page
    const isPageMovieDetail = pathname.includes('/movies/[movieId]');

    const cinema = domainConfiguration.cinema;
    const websiteConfiguration = cinema._embedded?.cinemaConfiguration?.websiteConfiguration;
    const navigationItems = websiteConfiguration?.navigation;
    const isNavVisible = (section: WebsiteNavigation) => isNavigationVisible(navigationItems, section);

    const shopLinks = getShopLinks(websiteConfiguration);
    const resolvedShopItems = shopLinks
        .map((link) => {
            const href = resolveShopLinkHref(link);
            if (!href) return null;
            const defaultLabelMap = {
                voucher: t('navigation.vouchers'),
                'snack-shop': t('navigation.snack-shop'),
                login: t('navigation.login')
            };
            return {
                href,
                title: link.customName || defaultLabelMap[link.type],
                type: link.type
            };
        })
        .filter((item): item is { href: string; title: string; type: (typeof shopLinks)[0]['type'] } => item !== null);

    const showShops = resolvedShopItems.length > 0 && isNavVisible(WebsiteNavigation.SHOPS);

    /**
     * The bottom navigation only fits five entries legibly on a phone — beyond that the labels
     * ("Gutscheine", "Aktuelles") run into each other. Rather than shrinking or clipping them,
     * entries are grouped behind dropdowns until the row is back within budget:
     *
     *   1. Specials folds into the program entry, which becomes a dropdown offering the full
     *      programme and the specials.
     *   2. Announcements, bar and info fold together into a "more" dropdown.
     *
     * Each step is applied only while the row is still over budget, and only when it actually
     * saves a slot — so a cinema with few entries keeps the plain, directly tappable row.
     */
    const showHome = isNavVisible(WebsiteNavigation.HOME);
    const showProgram = isNavVisible(WebsiteNavigation.PROGRAM);
    const showSpecials = hasShowingTags && isNavVisible(WebsiteNavigation.SPECIALS);
    const showDeals = isNavVisible(WebsiteNavigation.DEALS);
    const showAnnouncements = hasAnnouncements && isNavVisible(WebsiteNavigation.ANNOUNCEMENTS);
    const showBar = isNavVisible(WebsiteNavigation.BAR);
    const showInfo = !isInfoHiddenForCinema(cinema) && isNavVisible(WebsiteNavigation.INFO);

    const { collapseProgram, collapseMore } = planMobileNavigation({
        home: showHome,
        program: showProgram,
        specials: showSpecials,
        deals: showDeals,
        shops: showShops,
        announcements: showAnnouncements,
        bar: showBar,
        info: showInfo
    });

    const moreSections: NavigationDropdownItem[] = [
        ...(showAnnouncements
            ? [
                  {
                      href: '/announcements',
                      title: t('navigation.announcements'),
                      icon: <IconMegaphone24 className="flex-none" />,
                      item: 'announcements'
                  }
              ]
            : []),
        ...(showBar
            ? [
                  {
                      href: '/bar',
                      title: t('navigation.bar'),
                      icon: <IconBar24 className="flex-none" />,
                      item: 'bar'
                  }
              ]
            : []),
        ...(showInfo
            ? [
                  {
                      href: '/info',
                      title: t('navigation.info'),
                      icon: <IconInfo24 className="flex-none" />,
                      item: 'info'
                  }
              ]
            : [])
    ];

    const moreSectionRoutes = moreSections.map((section) => section.href);

    const element = (
        <div
            className={classnames(
                'relative',
                'h-72'
                // showNavigationMobile ? 'h-auto py-12' : 'h-0 py-0'
            )}>
            <div
                className={classnames('absolute top-0 left-0 h-full w-full', 'backdrop-blur-xl gradient-mask-t-70')}
                style={
                    domainConfiguration
                        ? {
                              backgroundColor:
                                  domainConfiguration.style.backgroundColorGradient ||
                                  domainConfiguration.style.backgroundColor,
                              opacity: 1
                          }
                        : undefined
                }></div>

            <div className="absolute top-0 z-modal-view w-full py-12">
                <TabGroup>
                    {/* Grid rather than flex: `auto-cols-fr` is `minmax(0, 1fr)`, so every entry gets an
                     *  identical column that its label cannot widen. Flex items default to
                     *  `min-width: auto`, which lets a long label ("Gutscheine") claim more than its
                     *  share and shift its neighbours off their centres. */}
                    <TabList className="w-full grid grid-flow-col auto-cols-fr items-center">
                        {showHome && (
                            <NavigationItemMobile
                                icon={<IconHome24 className="flex-none" />}
                                activeIcon={<IconHomeSolid24 />}
                                title={t('navigation.home')}
                                href="/"
                                item="home"
                            />
                        )}
                        {showProgram && collapseProgram && (
                            <NavigationDropdownMobile
                                icon={<IconProgram24 className="flex-none" />}
                                activeIcon={<IconProgramSolid24 className="flex-none" />}
                                triggerTitle={t('navigation.program')}
                                routes={['/program', '/specials']}
                                items={[
                                    {
                                        href: '/program',
                                        title: t('navigation.program-overview'),
                                        icon: <IconProgram24 className="flex-none" />,
                                        item: 'program'
                                    },
                                    {
                                        href: '/specials',
                                        title: t('navigation.specials'),
                                        icon: <IconStar24 className="flex-none" />,
                                        item: 'specials'
                                    }
                                ]}
                            />
                        )}
                        {showProgram && !collapseProgram && (
                            <NavigationItemMobile
                                icon={<IconProgram24 className="flex-none" />}
                                activeIcon={<IconProgramSolid24 className="flex-none" />}
                                title={t('navigation.program')}
                                href="/program"
                                item="program"
                            />
                        )}
                        {showSpecials && !collapseProgram && (
                            <NavigationItemMobile
                                icon={<IconStar24 className="flex-none" />}
                                activeIcon={<IconStarSolid24 className="flex-none" />}
                                title={t('navigation.specials')}
                                href="/specials"
                                item="specials"
                            />
                        )}
                        {showDeals && (
                            <NavigationItemMobile
                                icon={<IconDeals24 className="flex-none" />}
                                activeIcon={<IconDealsSolid24 className="flex-none" />}
                                title={t('navigation.deals')}
                                href="/deals"
                                item="deals"
                            />
                        )}
                        {showShops && resolvedShopItems.length === 1 && (
                            <NavigationItemMobile
                                icon={
                                    resolvedShopItems[0].type === 'snack-shop' ? (
                                        <IconPopcornBag24 className="flex-none" />
                                    ) : resolvedShopItems[0].type === 'login' ? (
                                        <IconProfile24 className="flex-none" />
                                    ) : (
                                        <IconVoucher24 className="flex-none" />
                                    )
                                }
                                title={resolvedShopItems[0].title}
                                href={resolvedShopItems[0].href}
                                item={resolvedShopItems[0].type}
                            />
                        )}
                        {showShops && resolvedShopItems.length > 1 && (
                            <ShopLinksDropdownMobile
                                items={resolvedShopItems}
                                triggerTitle={t('navigation.shop')}
                            />
                        )}
                        {showAnnouncements && !collapseMore && (
                            <NavigationItemMobile
                                icon={<IconMegaphone24 className="flex-none" />}
                                activeIcon={<IconMegaphoneSolid24 className="flex-none" />}
                                title={t('navigation.announcements')}
                                href="/announcements"
                                item="announcements"
                            />
                        )}
                        {showBar && !collapseMore && (
                            <NavigationItemMobile
                                icon={<IconBar24 className="flex-none" />}
                                activeIcon={<IconBarSolid24 className="flex-none" />}
                                title={t('navigation.bar')}
                                href="/bar"
                                item="bar"
                            />
                        )}
                        {showInfo && !collapseMore && (
                            <NavigationItemMobile
                                icon={<IconInfo24 className="flex-none" />}
                                activeIcon={<IconInfoSolid24 className="flex-none" />}
                                title={t('navigation.info')}
                                href="/info"
                                item="info"
                            />
                        )}
                        {collapseMore && (
                            <NavigationDropdownMobile
                                // The design system only ships the vertical (kebab) dots. Its three
                                // circles are symmetric about the centre, so a quarter turn yields
                                // exact horizontal dots — same radius and spacing, no new asset.
                                icon={<IconKebapMenu24 className="flex-none rotate-90" />}
                                triggerTitle={t('navigation.more')}
                                routes={moreSectionRoutes}
                                items={moreSections}
                            />
                        )}
                        {/*<NavigationItemMobile*/}
                        {/*    icon={<IconCineamoOutline24 className="flex-none" />}*/}
                        {/*    title={t('navigation.cineamo')}*/}
                        {/*    href={CINEAMO_WEBAPP_URL}*/}
                        {/*/>*/}
                    </TabList>
                </TabGroup>
            </div>
        </div>
    );

    if (isMobile && mobileMenuContainer && !isPageMovieDetail) {
        return ReactDOM.createPortal(element, mobileMenuContainer);
    }

    return null;
}

export default NavigationMobile;
